import { isRecord } from "utils/isRecord";

/**
 * Parsing for Scout sandbox export references.
 *
 * A `sandbox_export_file` tool result carries an `artifactKey` — the
 * conversation-scoped S3 key of the exported file. The download URL itself is
 * minted by the API when the user clicks (see `ScoutSandboxArtifactDownload`),
 * so nothing here ever needs a presigned URL to be intact.
 */

const ARTIFACT_KEY_PREFIX = "scout-artifacts/";

export interface SandboxArtifactRef {
  artifactKey: string;
  conversationId: string;
}

function readString(value: Record<string, unknown>, key: string): string | null {
  const v = value[key];
  return typeof v === "string" && v.length > 0 ? v : null;
}

/**
 * Validates the key layout written by the Scout runtime:
 * `scout-artifacts/<tenant segment>/<conversationId>/<stamp>-<filename>`.
 * The tail is sanitized to `[A-Za-z0-9._-]` at upload time, so a real key
 * has exactly four segments.
 */
function refFromKey(key: string): SandboxArtifactRef | null {
  if (!key.startsWith(ARTIFACT_KEY_PREFIX)) {
    return null;
  }
  const segments = key.split("/");
  if (segments.length !== 4 || segments.some((segment) => segment.length === 0)) {
    return null;
  }
  return { artifactKey: key, conversationId: segments[2] };
}

/**
 * Reads a download reference from a `sandbox_export_file` tool output. Returns
 * null when the output has no usable reference — the caller renders no
 * affordance. Results persisted before exports switched to keys carried a
 * presigned URL instead and produce no button; those exist only in test
 * conversations, and re-running the export replaces them.
 */
export function parseSandboxArtifactRef(toolOutput: unknown): SandboxArtifactRef | null {
  if (!isRecord(toolOutput)) {
    return null;
  }
  const artifactKey = readString(toolOutput, "artifactKey");
  if (!artifactKey) {
    return null;
  }
  return refFromKey(artifactKey);
}
